import React, {useMemo} from "react";
import {Box, Paper, Typography} from "@mui/material";
import {grey} from '@mui/material/colors';

import {TAG_COLORS} from "../../helpers/MemberTagHelper";
import {SENTIMENT} from "../../helpers/SessionHelper";

const COLORS = {
    BORDER_GRAY: grey[400],
    CATEGORY_HEADER_BG_WHITE: 'white',
    CATEGORY_HEADER_TEXT_BLACK: 'black',
};

const OPACITY = {
    DIMMED: 0.6,
    FULLY_OPAQUE: 1,
};

const BACKGROUND_COLORS = {
    CLICKABLE_HOVER: grey[200],
    SELECTED: grey[300],
    DEFAULT: 'white',
    TRANSPARENT: 'transparent',
};

const FONT_WEIGHTS = {
    SELECTED: 600,
    DEFAULT: 400,
};

const getHoverBackgroundColor = (isClickable, isSelected) => {
    if (isClickable) {
        return BACKGROUND_COLORS.CLICKABLE_HOVER;
    }

    if (isSelected) {
        return BACKGROUND_COLORS.SELECTED;
    }

    return BACKGROUND_COLORS.DEFAULT;
};

const styles = {
    box: {
        categoryHeader: {
            backgroundColor: COLORS.CATEGORY_HEADER_BG_WHITE,
            border: `1px solid ${COLORS.BORDER_GRAY}`,
            borderBottom: 'none',
            color: COLORS.CATEGORY_HEADER_TEXT_BLACK,
            flex: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
            padding: '0.25rem',
            position: 'sticky',
            textAlign: 'center',
            top: 0,
            zIndex: 3,
        },
        categoryHeadersRow: {
            display: 'flex',
            flexDirection: 'row',
            position: 'sticky',
            top: '0rem',
            backgroundColor: COLORS.CATEGORY_HEADER_BG_WHITE,
            zIndex: 2,
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '22rem',
            minHeight: '22rem',
            overflow: 'auto',
        },
        sentimentHeader: {
            border: `1px solid ${COLORS.BORDER_GRAY}`,
            color: COLORS.CATEGORY_HEADER_BG_WHITE,
            flex: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
            padding: '0.5rem',
            textAlign: 'center',

        },
        sentimentHeadersRow: {
            display: 'flex',
            flexDirection: 'row',
            position: 'sticky',
            top: '1.7rem',
            zIndex: 2,
        },
        tagCell: {
            border: `1px solid ${COLORS.BORDER_GRAY}`,
            borderTop: 'none',
            color: COLORS.CATEGORY_HEADER_TEXT_BLACK,
            flex: 1,
            fontSize: '0.75rem',
            minHeight: '1rem',
            padding: '0.1rem',
        },
        tagRow: {
            display: 'flex',
            flexDirection: 'row',
        },
    },
    typography: {
        sectionTitle: {
            fontSize: '0.75rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
        },
    },
};

export default function AddTagsTable({availableTags, categories, onTagSelection, isFutureSession = false}) {

    const getTagsByCategory = useMemo(() => {
        const categoryMap = {};

        categories.forEach(category => {
            categoryMap[category] = [];
        });

        availableTags.forEach(tag => {
            if (categoryMap[tag.category]) {
                categoryMap[tag.category].push(tag);
            }
        });

        return categoryMap;
    }, [categories, availableTags]);

    const getCategoryData = useMemo(() => {
        return categories.map(category => {
            const positive = getTagsByCategory[category]?.filter(tag => tag.sentiment === SENTIMENT.POSITIVE) || [];
            const negative = getTagsByCategory[category]?.filter(tag => tag.sentiment === SENTIMENT.NEGATIVE) || [];

            return {category, negative, positive};
        });
    }, [categories, getTagsByCategory]);

    const maxTagsInAnyCategory = useMemo(() => {
        return Math.max(...getCategoryData.map(cat =>
            Math.max(cat.positive.length, cat.negative.length)
        ), 0);
    }, [getCategoryData]);

    const buildTagRows = useMemo(() => {
        const rows = [];
        for (let rowIndex = 0; rowIndex < maxTagsInAnyCategory; rowIndex++) {
            const rowCells = [];
            getCategoryData.forEach(({positive, negative}) => {
                rowCells.push(positive[rowIndex] || null);
                rowCells.push(negative[rowIndex] || null);
            });
            rows.push(rowCells);
        }
        return rows;
    }, [getCategoryData, maxTagsInAnyCategory]);

    const renderTagCell = (tag, cellIndex) => {
        const isClickable = tag !== null && !isFutureSession;
        const isSelected = tag?.isSelected || false;
        return (
            <Box
                key={`${cellIndex}-${tag?.id || 'empty'}`}
                onClick={() => isClickable && onTagSelection(tag)}
                sx={{
                    ...styles.box.tagCell,
                    cursor: isClickable ? 'pointer' : 'default',
                    backgroundColor: isSelected ? BACKGROUND_COLORS.SELECTED : BACKGROUND_COLORS.TRANSPARENT,
                    fontWeight: isSelected ? FONT_WEIGHTS.SELECTED : FONT_WEIGHTS.DEFAULT,
                    opacity: isFutureSession && tag !== null ? OPACITY.DIMMED : OPACITY.FULLY_OPAQUE,
                    '&:hover': {
                        backgroundColor: getHoverBackgroundColor(isClickable, isSelected),
                    },
                }}>
                {tag?.label || ''}
            </Box>
        );
    };

    return (
        <>
            <Typography sx={styles.typography.sectionTitle}>Add Tags +</Typography>
            <Paper component={Box} variant="outlined" sx={styles.box.container}>
                <Box sx={styles.box.categoryHeadersRow}>
                    {
                        getCategoryData.map(({category}) => (
                          <Box key={`${category}-header`} sx={{...styles.box.categoryHeader, flex: 2}}>
                              {category}
                          </Box>
                        ))
                    }
                </Box>

                <Box sx={styles.box.sentimentHeadersRow}>
                    {
                        getCategoryData.map(({category}) => (
                          <React.Fragment key={`${category}-sentiment-headers`}>
                              <Box sx={{...styles.box.sentimentHeader, backgroundColor: TAG_COLORS.POSITIVE}}/>
                              <Box sx={{...styles.box.sentimentHeader, backgroundColor: TAG_COLORS.NEGATIVE}}/>
                          </React.Fragment>
                        ))
                    }
                </Box>

                {
                    buildTagRows.map((rowCells, rowIndex) => (
                      <Box key={`row-${rowIndex}`} sx={styles.box.tagRow}>
                          {rowCells.map((tag, cellIndex) => renderTagCell(tag, cellIndex))}
                      </Box>
                    ))
                }
            </Paper>
        </>
    );
}